// @flow

import React, { Component } from 'react'
import { containerIds } from 'redux/modules/ui'
import { wwwUrl, accountLoginUrl } from 'frontend_core/dist/helpers/url'
import { isMobile } from 'common/screen'
import { type Room as RoomType } from 'types/room'
import { type PropTypes } from './index'

export type RoomUseCaseType = {
  initialize: Function,
  setRoom: Function,
  checkAttention: Function,
  openFavoriteList: Function,
  addFavorite: Function,
  toggleShare: Function,
  displayAtSlideShow: Function,
  setSlideCurrentIndex: Function,
  dismissSlideShow: Function,
  openAmenity: Function,
  openOptionItem: Function,
  displayBottomRequestButton: Function,
  hideBottomRequestButton: Function,
  openInquiryModal: Function,
}

export default (WrappedComponent: any) =>
  class RoomUseCase extends Component<*, PropTypes, *> {
    /**
     * 初期処理
     */
    initialize = () => {
      const { actions } = this.props
      actions.setUIState(containerIds.ROOM, {
        isShowingModal: false,
        currentDisplayingIndex: 0,
        isFacilityOpen: false,
        isServiceOpen: false,
        isAmenityOpen: false,
        isFoodOpen: false,
        isTrashOpen: false,
        isShowingShare: false,
        isLoadGoogleMapApi: false,
        isAttentionPopupOpen: false,
      })
    }

    /**
     * GraphQLで取得したRoomオブジェクトをstoreへ保存
     */
    setRoom = () => {
      const { actions, data } = this.props
      actions.setRoomDetail(data.room)
    }

    /**
     * 何人の人が利用しましたポップアップ表示チェック処理
     */
    checkAttention = () => {
      if (this.props.data.room.usedGuestsCount > 0) {
        this.props.actions.setUIState(containerIds.ROOM, {
          isAttentionPopupOpen: true,
        })
        setTimeout(() => {
          this.props.actions.setUIState(containerIds.ROOM, {
            isAttentionPopupOpen: false,
          })
        }, 18000)
      }
    }

    /**
     * SNSシェアの表示切り替え
     */
    toggleShare = (e: Event) => {
      e.preventDefault()
      const { ui, actions } = this.props
      actions.setUIState(containerIds.ROOM, {
        isShowingShare: !ui.isShowingShare,
      })
    }

    /**
     * お気に入りリスト表示
     */
    openFavoriteList = (targetRoom: RoomType) => {
      if (!this.props.data.viewer) {
        // ログインしてなければログイン画面へリダイレクト
        window.location.href = accountLoginUrl(this.props.config)
      } else {
        const { actions } = this.props
        actions.setUIState(containerIds.ROOM, {
          room: targetRoom,
          isOpenFavoriteSelectModal: true,
        })
      }
    }

    /**
     * お気に入りに追加
     */
    addFavorite = (e: Event, room: RoomType, favoriteListId?: string) => {
      e.preventDefault()
      const currentUser = this.props.data.viewer
      if (!currentUser) {
        window.location = accountLoginUrl(this.props.config)
      } else if (room.isFavorite) {
        // すでにお気に入りならお気に入りリストを開く
        this.openFavoriteList(room)
      } else {
        const { actions } = this.props
        actions.addFavorite({
          userId: currentUser.id,
          roomId: room.id,
          favoriteListId,
        })
      }
    }

    /**
     * 指定番目のスライドショーを表示する
     */
    displayAtSlideShow = (e: Event, index: number = 0) => {
      e.preventDefault()
      const { actions } = this.props
      const roomThumbnails = this.props.data.room.thumbnails.results

      // サムネイルのアクティブ判定用
      let nextIndex = index
      if (nextIndex < 0) {
        nextIndex = roomThumbnails.length - 1
      } else if (nextIndex >= roomThumbnails.length) {
        nextIndex = 0
      }
      actions.setUIState(containerIds.ROOM, {
        isShowingModal: true,
        currentDisplayingIndex: nextIndex,
      })
    }

    /**
     * 指定番目のスライドショーの選択通知用メソッド
     */
    setSlideCurrentIndex = (index: number) => {
      const { actions } = this.props
      actions.setUIState(containerIds.ROOM, {
        currentDisplayingIndex: index,
      })
    }

    /**
     * スライドショーを非表示
     */
    dismissSlideShow = (e: Event) => {
      e.preventDefault()
      const { actions } = this.props
      actions.setUIState(containerIds.ROOM, { isShowingModal: false })
    }

    /**
     * アメニティの続きを見る
     */
    openAmenity = (e: Event) => {
      e.preventDefault()
      const { actions } = this.props
      actions.setUIState(containerIds.ROOM, { isAmenityOpen: true })
    }

    /**
     * オプションの続きを見る
     */
    openOptionItem = (e: Event) => {
      e.preventDefault()
      const { actions } = this.props
      actions.setUIState(containerIds.ROOM, { isOpenOptionList: true })
    }

    /**
     * モバイル用下部予約リクエストボタンを表示する
     */
    displayBottomRequestButton = () => {
      if (isMobile()) {
        const { actions, ui } = this.props
        if (!ui.isDisplayBottomRequestButtom) {
          actions.setUIState(containerIds.ROOM, {
            isDisplayBottomRequestButtom: true,
          })
        }
      }
    }

    /**
     * モバイル用下部予約リクエストボタンを非表示にする
     */
    hideBottomRequestButton = () => {
      if (isMobile()) {
        const { actions, ui } = this.props
        if (ui.isDisplayBottomRequestButtom) {
          actions.setUIState(containerIds.ROOM, {
            isDisplayBottomRequestButtom: false,
          })
        }
      }
    }

    isShowableInquiryModal = () => {
      const { user, ui } = this.props
      const contact = ui.contact || {}
      return (
        (user.user || {}).has_confirmed_phone &&
        typeof contact.familyName === 'string' &&
        typeof contact.givenName === 'string' &&
        typeof contact.postalCode === 'string' &&
        typeof contact.state === 'string' &&
        typeof contact.city === 'string' &&
        typeof contact.address1 === 'string'
      )
    }

    /**
     * お問い合わせモーダルを表示
     */
    openInquiryModal = (e: Event) => {
      e.preventDefault()
      const { actions, data } = this.props
      if (!data.viewer) {
        window.location.href = accountLoginUrl(this.props.config)
      } else if (this.isShowableInquiryModal()) {
        actions.setUIState(containerIds.ROOM, { isOpenInquiry: true })
      } else {
        actions.setUIState(containerIds.ROOM, { isOpenUserContact: true })
      }
    }

    render() {
      return <WrappedComponent {...this.props} usecase={this} />
    }
  }



// WEBPACK FOOTER //
// ./src/containers/pages/RoomPage/usecase.jsx